<script setup>
import { ref, computed } from 'vue'
import TableList from '../others/TableList.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'
import Commons from '@/shared/utils/commons'
import NotificationForm from '../notifications/NotificationForm.vue'
import SlideDetailView from '../others/SlideDetailView.vue'
import { cloneDeep } from 'lodash-es'

// Reactive data
const detailData = ref(null)
const showNotificationForm = ref(false)
const saveNotificationTrigger = ref(null)
const isSubmitting = ref(false)

// Table columns
const cols = [
  {
    name: 'Titre & Description',
    id: 'title',

    value(el) {
      let desc = el.description || '-'
      desc = desc.replace(/(.{1,50})(\s+|$)/g, '$1<br>')
      return [el.title || '-', desc]
    },
  },
  {
    name: 'Date',
    id: 'date',

    value(el) {
      return Commons.time(el.date, 'DD MMM YYYY à hh:mm')
    },
  },
  // {
  //   name: 'Zone',
  //   value(el) {
  //     return el.zone?.join('; ') || '-'
  //   },
  // },
  {
    name: 'Cible',
    value(el) {
      const map = {
        all: 'Tout le monde',
        restaurant: 'Restaurants',
        user: 'Clients',
        rider: 'Livreurs',
        customersOfRank: 'Clients de rang',
      }

      const base = map[el.target] || el.target || '-'

      return base
    },
  },
  {
    name: 'Statut',
    id: 'status',
    value(el) {
      const statusMap = {
        pending: '<span class="text-yellow-600 font-medium">En attente</span>',
        sent: '<span class="text-green-600 font-medium">Envoyé</span>',
        scheduled: '<span class="text-blue-600 font-medium">Programmé</span>',
        failed: '<span class="text-red-600 font-medium">Échoué</span>',
      }
      return (
        statusMap[el.status] ||
        '<span class="text-gray-600 font-medium">Inconnu</span>'
      )
    },
  },
  {
    name: 'Actions',
    id: 'action',
    icon: () => '/images/icons/dots.png',
    options: el => {
      const actions = []

      // Renvoyer pour les notifications envoyées ou échouées
      if (el.status === 'sent' || el.status === 'failed') {
        actions.push({ name: 'Renvoyer', action: 'resend' })
      }

      // Annuler uniquement pour les notifications programmées
      if (el.status === 'scheduled') {
        actions.push({ name: 'Annuler', action: 'cancel' })
      }

      // Pas de modification ou suppression pour les autres statuts
      return actions
    },
    onClick(option, el) {
      handleNotificationAction(el, option)
    },
    value() {},
  },
]

// Table buttons
const buttons = [
  {
    title: '&plus; Nouvelle notification',
    onClick() {
      detailData.value = {}
      showNotificationForm.value = true
    },
  },
]

// Methods
const handleNotificationAction = (notification, action) => {
  switch (action.action) {
    case 'resend':
      resendNotification(notification)
      break
    case 'cancel':
      cancelScheduledNotification(notification)
      break
  }
}

const resendNotification = notification => {
  // Créer une nouvelle notification à partir de l'existante
  detailData.value = {
    title: notification.title,
    description: notification.description,
    zone: notification.zone || [],
    target: notification.target,
    targetRank: notification.targetRankName || notification.targetRankId || '',
    priority: notification.priority || 'normal',
    // Ne PAS copier id, date, status pour créer une nouvelle notification
  }
  showNotificationForm.value = true
}

const cancelScheduledNotification = async notification => {
  try {
    await saveNotificationTrigger.value.trigger({
      id: notification.id,
      action: 'cancel',
    })

    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Succès',
      body: 'Notification programmée annulée',
    })
  } catch (error) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: "Impossible d'annuler la notification",
    })
  }
}

const handleNotificationSubmit = async formData => {
  isSubmitting.value = true

  try {
    // Déterminer si c'est un renvoi ou une création
    const isResend = detailData.value && !detailData.value.id
    const action = isResend ? 'resend' : 'create'

    await saveNotificationTrigger.value.trigger({
      ...formData,
      action,
    })

    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Succès',
      body: isResend ? 'Notification renvoyée avec succès' : 'Notification créée avec succès',
    })

    // Close form and reset data
    showNotificationForm.value = false
    detailData.value = null
  } catch (error) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: 'Impossible de créer la notification',
    })
  } finally {
    isSubmitting.value = false
  }
}

const handleNotificationFormCancel = () => {
  showNotificationForm.value = false
  detailData.value = null
}

const onNotificationSuccess = () => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'Effectué avec success',
  })
}

const onNotificationError = error => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur',
    body: `Erreur: ${error.result?.message || 'Erreur inconnue'}`,
  })
}
</script>

<template>
  <div class="p-5 h-full overflow-auto bg-gray-50">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-800 mb-2">
        Gestion des Notifications
      </h1>
      <p class="text-gray-600">
        Créez et gérez les notifications envoyées aux utilisateurs
      </p>
    </div>

    <!-- Notifications Table -->
    <TableList
      title="Notifications"
      dataKey="notifications"
      hasSearch
      :cols="cols"
      :buttons="buttons"
    ></TableList>

    <!-- Notification Form Modal -->
    <SlideDetailView
      :isVisible="showNotificationForm"
      @hide="handleNotificationFormCancel"
    >
      <NotificationForm
        :initialData="detailData"
        @submit="handleNotificationSubmit"
        @cancel="handleNotificationFormCancel"
      />
    </SlideDetailView>

    <!-- Action Triggers -->
    <ActionTrigger
      dataKey="saveNotification"
      ref="saveNotificationTrigger"
      @success="onNotificationSuccess"
      @failed="onNotificationError"
    />
  </div>
</template>
